/**
 * WaveSurfer wrapper service for audio-loop-player
 * Handles waveform visualization and synchronization with loops
 */

import WaveSurfer from 'wavesurfer.js';
import Hover from 'wavesurfer.js/dist/plugins/hover.js';
import RegionsPlugin, { type Region } from 'wavesurfer.js/dist/plugins/regions.js';
import Zoom from 'wavesurfer.js/dist/plugins/zoom.js';

export interface WaveformConfig {
    container: HTMLElement;
    waveColor?: string;
    progressColor?: string;
    cursorColor?: string;
    barWidth?: number;
    barGap?: number;
    barRadius?: number;
    height?: number;
    normalize?: boolean;
    enableZoom?: boolean;
    enableHover?: boolean;
    onRegionCreated?: (start: number, end: number) => void;
    onRegionUpdated?: (id: string, start: number, end: number) => void;
}

export interface LoopRegion {
    id: string;
    start: number;
    end: number;
    color: string;
    label?: string;
}

/**
 * WaveSurfer service wrapper
 */
export class WaveformService {
    private wavesurfer: WaveSurfer | null = null;
    private regionsPlugin: RegionsPlugin | null = null;
    private hoverPlugin: Hover | null = null;
    private zoomPlugin: Zoom | null = null;
    private audioElement: HTMLAudioElement | null = null;
    private onRegionCreatedCallback?: (start: number, end: number) => void;
    private onRegionUpdatedCallback?: (id: string, start: number, end: number) => void;
    private isCreatingRegion = false;
    private regionStart: number | null = null;
    private hoverLabel: HTMLElement | null = null;
    private hoverLine: HTMLElement | null = null;
    private lastMousePosition: { clientX: number; clientY: number } | null = null;
    private lastHoverTime: number | null = null;  // Time in seconds that was being hovered

    /**
     * Initialize WaveSurfer with given configuration
     */
    async init(config: WaveformConfig, audioElement: HTMLAudioElement): Promise<void> {
        if (this.wavesurfer) {
            this.destroy();
        }

        this.audioElement = audioElement;
        this.onRegionCreatedCallback = config.onRegionCreated;
        this.onRegionUpdatedCallback = config.onRegionUpdated;

        // Create plugins
        this.regionsPlugin = RegionsPlugin.create();

        // Setup region update listeners
        if (this.onRegionUpdatedCallback) {
            this.regionsPlugin.on('region-updated', (region: Region) => {
                this.onRegionUpdatedCallback?.(region.id, region.start, region.end);
            });
        }

        const plugins: any[] = [this.regionsPlugin];

        // Skip the Hover plugin - we'll create our own
        // if (config.enableHover !== false) { ... }

        // Add zoom plugin if enabled
        if (config.enableZoom) {
            this.zoomPlugin = Zoom.create({
                scale: 0.5,
                maxZoom: 500,
            });
            plugins.push(this.zoomPlugin);
        }

        // Initialize WaveSurfer with the existing media element
        this.wavesurfer = WaveSurfer.create({
            container: config.container,
            waveColor: config.waveColor || 'rgba(128, 128, 128, 0.5)',
            progressColor: config.progressColor || 'rgba(58, 166, 255, 0.7)',
            cursorColor: config.cursorColor || '#3ea6ff',
            barWidth: config.barWidth || 2,
            barGap: config.barGap || 1,
            barRadius: config.barRadius || 2,
            height: config.height || 100,
            normalize: config.normalize ?? true,
            autoCenter: false,  // Don't auto-scroll by default
            media: audioElement,
            plugins,
        });

        // Setup region creation on shift+drag
        this._setupRegionCreation();

        // Setup custom hover with manual time calculation
        if (config.enableHover !== false) {
            this._setupCustomHover(config.container, config.cursorColor || '#3ea6ff');
        }

        // Wait for WaveSurfer to be ready, then manually sync duration
        return new Promise((resolve, reject) => {
            if (!this.wavesurfer) {
                reject(new Error('WaveSurfer not initialized'));
                return;
            }

            // Add timeout to prevent hanging forever
            const timeout = setTimeout(() => {
                this.wavesurfer?.un('ready', onReady);
                this.wavesurfer?.un('error', onError);
                reject(new Error('WaveSurfer init timeout - ready event never fired'));
            }, 10000);

            const onReady = () => {
                clearTimeout(timeout);
                this.wavesurfer?.un('ready', onReady);
                this.wavesurfer?.un('error', onError);

                resolve();
            };

            const onError = (error: Error) => {
                clearTimeout(timeout);
                this.wavesurfer?.un('ready', onReady);
                this.wavesurfer?.un('error', onError);
                console.error('[WaveformService] WaveSurfer error:', error);
                reject(error);
            };

            this.wavesurfer.on('ready', onReady);
            this.wavesurfer.on('error', onError);
        });
    }

    /**
     * Setup region creation by shift+dragging on waveform
     */
    private _setupRegionCreation() {
        if (!this.wavesurfer) return;

        const container = this.wavesurfer.getWrapper();

        const onMouseDown = (e: MouseEvent) => {
            // Only start region creation on shift+click
            if (!e.shiftKey || !this.wavesurfer || !this.audioElement) return;

            this.isCreatingRegion = true;
            const duration = this.audioElement.duration;
            const rect = container.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const percent = x / rect.width;
            this.regionStart = percent * duration;

            e.preventDefault();
        };

        const onMouseMove = (e: MouseEvent) => {
            if (!this.isCreatingRegion || this.regionStart === null) return;
            e.preventDefault();
        };

        const onMouseUp = (e: MouseEvent) => {
            if (!this.isCreatingRegion || this.regionStart === null || !this.audioElement) return;

            const duration = this.audioElement.duration;
            const rect = container.getBoundingClientRect();
            const x = e.clientX - rect.left;
            const percent = x / rect.width;
            const regionEnd = percent * duration;

            // Create region if there's a meaningful duration (at least 0.1s)
            const start = Math.min(this.regionStart, regionEnd);
            const end = Math.max(this.regionStart, regionEnd);

            if (end - start > 0.1) {
                this.onRegionCreatedCallback?.(start, end);
            }

            this.isCreatingRegion = false;
            this.regionStart = null;
            e.preventDefault();
        };

        container.addEventListener('mousedown', onMouseDown);
        container.addEventListener('mousemove', onMouseMove);
        container.addEventListener('mouseup', onMouseUp);

        // Store cleanup
        this._cleanupRegionCreation = () => {
            container.removeEventListener('mousedown', onMouseDown);
            container.removeEventListener('mousemove', onMouseMove);
            container.removeEventListener('mouseup', onMouseUp);
        };
    }

    private _cleanupRegionCreation?: () => void;
    private _cleanupCustomHover?: () => void;

    /**
     * Setup custom hover tooltip with manual time calculation
     */
    private _setupCustomHover(container: HTMLElement, cursorColor: string) {
        if (!this.audioElement) return;

        // Get the waveform wrapper (the actual waveform canvas container)
        const wrapper = this.wavesurfer?.getWrapper();
        if (!wrapper) return;

        // Create hover line
        this.hoverLine = document.createElement('div');
        Object.assign(this.hoverLine.style, {
            position: 'absolute',
            zIndex: '10',
            left: '0',
            top: '0',
            height: '100%',
            pointerEvents: 'none',
            borderLeft: `2px solid ${cursorColor}`,
            opacity: '0',
            transition: 'opacity .1s ease-in',
        });

        // Create label
        this.hoverLabel = document.createElement('span');
        Object.assign(this.hoverLabel.style, {
            display: 'block',
            backgroundColor: '#555',
            color: '#fff',
            fontSize: '11px',
            padding: '2px 3px',
            borderRadius: '2px',
            whiteSpace: 'nowrap',
        });
        this.hoverLine.appendChild(this.hoverLabel);

        wrapper.appendChild(this.hoverLine);

        const updateHover = (clientX: number) => {
            if (!this.audioElement || !this.hoverLine || !this.hoverLabel) return;

            const rect = wrapper.getBoundingClientRect();
            const x = clientX - rect.left;
            const scrollLeft = wrapper.scrollLeft || 0;
            const duration = this.audioElement.duration;

            if (!duration) return;

            // Calculate time based on position and scroll
            const totalWidth = wrapper.scrollWidth;
            const percent = (x + scrollLeft) / totalWidth;
            const seconds = duration * Math.min(1, Math.max(0, percent));

            // Store the time being hovered for zoom updates
            this.lastHoverTime = seconds;

            // Format time
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = Math.floor(seconds % 60);

            const timeStr = hours > 0
                ? `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
                : `${minutes}:${secs.toString().padStart(2, '0')}`;

            this.hoverLabel.textContent = timeStr;

            // Position line at mouse X
            const lineX = Math.min(rect.width - 2, Math.max(0, x));
            this.hoverLine.style.transform = `translateX(${lineX}px)`;
            this.hoverLine.style.opacity = '1';

            // Position label (prefer left if space available)
            const labelWidth = this.hoverLabel.offsetWidth;
            const preferLeft = lineX - labelWidth - 2 > 0;
            this.hoverLabel.style.transform = preferLeft ? `translateX(-${labelWidth + 2}px)` : '';
        };

        const updateHoverAfterZoom = () => {
            // After zoom, recalculate position based on the stored time
            if (!this.audioElement || !this.hoverLine || !this.hoverLabel || this.lastHoverTime === null) return;
            if (!this.lastMousePosition) return;

            const rect = wrapper.getBoundingClientRect();
            const scrollLeft = wrapper.scrollLeft || 0;
            const duration = this.audioElement.duration;

            if (!duration) return;

            // Calculate new position from stored time
            const totalWidth = wrapper.scrollWidth;
            const percent = this.lastHoverTime / duration;
            const xPosition = (percent * totalWidth) - scrollLeft;

            // Format time (already calculated)
            const seconds = this.lastHoverTime;
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            const secs = Math.floor(seconds % 60);

            const timeStr = hours > 0
                ? `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
                : `${minutes}:${secs.toString().padStart(2, '0')}`;

            this.hoverLabel.textContent = timeStr;

            // Position line at calculated X
            const lineX = Math.min(rect.width - 2, Math.max(0, xPosition));
            this.hoverLine.style.transform = `translateX(${lineX}px)`;
            this.hoverLine.style.opacity = '1';

            // Position label
            const labelWidth = this.hoverLabel.offsetWidth;
            const preferLeft = lineX - labelWidth - 2 > 0;
            this.hoverLabel.style.transform = preferLeft ? `translateX(-${labelWidth + 2}px)` : '';
        };

        const onMouseMove = (e: MouseEvent) => {
            this.lastMousePosition = { clientX: e.clientX, clientY: e.clientY };
            updateHover(e.clientX);
        };

        const onMouseLeave = () => {
            if (this.hoverLine) {
                this.hoverLine.style.opacity = '0';
            }
            this.lastMousePosition = null;
            this.lastHoverTime = null;
        };

        // Update hover position when zoom changes
        const onZoom = () => {
            if (this.lastMousePosition && this.lastHoverTime !== null) {
                updateHoverAfterZoom();
            }
        };

        wrapper.addEventListener('pointermove', onMouseMove);
        wrapper.addEventListener('pointerleave', onMouseLeave);
        this.wavesurfer?.on('zoom', onZoom);

        this._cleanupCustomHover = () => {
            wrapper.removeEventListener('pointermove', onMouseMove);
            wrapper.removeEventListener('pointerleave', onMouseLeave);
            this.wavesurfer?.un('zoom', onZoom);
            this.hoverLine?.remove();
            this.hoverLine = null;
            this.hoverLabel = null;
            this.lastMousePosition = null;
            this.lastHoverTime = null;
        };
    }

    /**
     * Set zoom level (pixels per second)
     */
    setZoom(pxPerSec: number) {
        // WaveSurfer zoom method
        this.wavesurfer?.zoom(pxPerSec);
    }

    /**
     * Add or update a loop region
     */
    addOrUpdateRegion(loop: LoopRegion): Region | null {
        if (!this.regionsPlugin) return null;

        // Check if region already exists
        const existingRegion = this.regionsPlugin.getRegions().find(r => r.id === loop.id);

        if (existingRegion) {
            // Update existing region
            existingRegion.setOptions({
                start: loop.start,
                end: loop.end,
                color: this.hexToRgba(loop.color, 0.3),
            });
            return existingRegion;
        } else {
            // Create new region
            return this.regionsPlugin.addRegion({
                id: loop.id,
                start: loop.start,
                end: loop.end,
                color: this.hexToRgba(loop.color, 0.3),
                drag: false,
                resize: true,
            });
        }
    }

    /**
     * Get all regions
     */
    getRegions(): Region[] {
        return this.regionsPlugin?.getRegions() ?? [];
    }

    /**
     * Remove a loop region
     */
    removeRegion(loopId: string): void {
        if (!this.regionsPlugin) return;

        const region = this.regionsPlugin.getRegions().find(r => r.id === loopId);
        if (region) {
            region.remove();
        }
    }

    /**
     * Clear all regions
     */
    clearRegions(): void {
        this.regionsPlugin?.clearRegions();
    }

    /**
     * Set active region (highlight)
     */
    setActiveRegion(loopId: string | null): void {
        if (!this.regionsPlugin) return;

        const regions = this.regionsPlugin.getRegions();
        regions.forEach(region => {
            // Reset all regions to default opacity
            const originalColor = region.color.replace(/[\d.]+\)$/g, '0.3)');
            region.setOptions({ color: originalColor });
        });

        if (loopId) {
            const activeRegion = regions.find(r => r.id === loopId);
            if (activeRegion) {
                // Highlight active region with higher opacity
                const highlightColor = activeRegion.color.replace(/[\d.]+\)$/g, '0.5)');
                activeRegion.setOptions({ color: highlightColor });
            }
        }
    }

    /**
     * Get current playback position (for sync)
     */
    getCurrentTime(): number {
        return this.wavesurfer?.getCurrentTime() ?? 0;
    }

    /**
     * Seek to time position
     */
    seekTo(time: number): void {
        if (!this.wavesurfer || !this.audioElement) return;

        const duration = this.audioElement.duration;
        if (duration > 0) {
            // WaveSurfer seekTo expects a value between 0 and 1
            this.wavesurfer.seekTo(time / duration);
        }
    }

    /**
     * Zoom waveform to show specific time range
     */
    zoom(minPxPerSec: number): void {
        this.wavesurfer?.zoom(minPxPerSec);
    }

    /**
     * Enable or disable auto-centering (horizontal scroll following playback)
     */
    setAutoCenter(enabled: boolean): void {
        if (this.wavesurfer) {
            (this.wavesurfer as any).options.autoCenter = enabled;
        }
    }

    /**
     * Check if waveform is ready
     */
    isReady(): boolean {
        return this.wavesurfer !== null;
    }

    /**
     * Check if a time range fits in the current visible view
     */
    doesLoopFitInView(start: number, end: number): boolean {
        if (!this.wavesurfer || !this.audioElement) return false;

        const container = this.wavesurfer.getWrapper();
        const containerWidth = container.clientWidth;
        const duration = this.audioElement.duration;

        // Get current zoom level (px per second)
        const zoom = (this.wavesurfer as any).options.minPxPerSec || 0;

        if (zoom === 0) {
            // Not zoomed, entire waveform is visible
            return true;
        }

        // Calculate loop width in pixels
        const loopDuration = end - start;
        const loopWidthPx = loopDuration * zoom;

        // Loop fits if its width is less than container width
        return loopWidthPx <= containerWidth;
    }

    /**
     * Listen for WaveSurfer events
     */
    on(event: any, callback: (...args: any[]) => void): void {
        this.wavesurfer?.on(event as any, callback);
    }

    /**
     * Remove event listener
     */
    off(event: any, callback: (...args: any[]) => void): void {
        this.wavesurfer?.un(event as any, callback);
    }

    /**
     * Destroy WaveSurfer instance
     */
    destroy(): void {
        this._cleanupRegionCreation?.();
        this._cleanupCustomHover?.();
        this.wavesurfer?.destroy();
        this.wavesurfer = null;
        this.regionsPlugin = null;
        this.hoverPlugin = null;
        this.zoomPlugin = null;
        this.audioElement = null;
        this.onRegionCreatedCallback = undefined;
        this.onRegionUpdatedCallback = undefined;
    }

    /**
     * Convert hex color to rgba with opacity
     */
    private hexToRgba(hex: string, alpha: number): string {
        // Remove # if present
        hex = hex.replace('#', '');

        // Parse hex
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
}
